import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { loanService } from '../../services/loanService';
import { formatCurrency, formatDate } from '../../utils/helpers';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const STATUS_META = {
  paid:     { label: 'Paid',         bg: 'rgba(0,200,120,0.12)',  color: '#00C878' },
  overdue:  { label: 'Overdue',      bg: 'rgba(255,71,87,0.12)',  color: '#FF4757' },
  partial:  { label: 'Partial',      bg: 'rgba(245,166,35,0.12)', color: '#F5A623' },
  pending:  { label: 'Pending',      bg: 'rgba(255,255,255,0.06)', color: 'var(--text-3)' },
  waived:   { label: 'Waived',       bg: 'rgba(91,142,255,0.12)', color: '#5B8EFF' },
  awaiting: { label: 'Awaiting Confirmation', bg: 'rgba(91,142,255,0.12)', color: '#5B8EFF' },
};

function StatusPill({ status }) {
  const m = STATUS_META[status] || STATUS_META.pending;
  return (
    <span style={{ padding: '2px 8px', borderRadius: 99, fontSize: '0.65rem', fontWeight: 700, background: m.bg, color: m.color, whiteSpace: 'nowrap' }}>
      {m.label}
    </span>
  );
}

function PayModal({ schedule, loanId, onClose }) {
  const qc = useQueryClient();
  const [amount, setAmount] = useState(
    (parseFloat(schedule.amount_due) - parseFloat(schedule.amount_paid || 0)).toFixed(2)
  );
  const [method, setMethod] = useState('bank_transfer');
  const [err, setErr] = useState('');

  const mutation = useMutation({
    mutationFn: (payload) => loanService.makePayment(loanId, payload),
    onSuccess: () => {
      // Invalidate both the standalone schedule view AND the embedded loan detail
      qc.invalidateQueries({ queryKey: ['repayment-schedule', loanId] });
      qc.invalidateQueries({ queryKey: ['loan-detail', loanId] });
      qc.invalidateQueries({ queryKey: ['my-loans'] });
      onClose();
    },
    onError: (e) => setErr(e.response?.data?.message || 'Payment failed.'),
  });

  const remaining = parseFloat(schedule.amount_due) + parseFloat(schedule.late_fee || 0) - parseFloat(schedule.amount_paid || 0);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div className="card" style={{ width: 420 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <h2 style={{ fontSize: '1rem', fontWeight: 700 }}>Make Payment</h2>
          <button className="btn btn-ghost" style={{ padding: '0.25rem 0.5rem' }} onClick={onClose}>✕</button>
        </div>

        <div style={{ background: 'var(--surface-alt)', borderRadius: 8, padding: '0.75rem', marginBottom: '1rem', fontSize: '0.8rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ color: 'var(--text-3)' }}>Installment #{schedule.installment_number}</span>
            <span style={{ color: 'var(--text-3)' }}>Due: {formatDate(schedule.due_date)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: 'var(--text-2)' }}>Amount Due</span>
            <span style={{ fontWeight: 700, color: 'var(--text-1)' }}>{formatCurrency(schedule.amount_due)}</span>
          </div>
          {parseFloat(schedule.late_fee || 0) > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 2 }}>
              <span style={{ color: '#FF4757' }}>Late Fee</span>
              <span style={{ fontWeight: 600, color: '#FF4757' }}>{formatCurrency(schedule.late_fee)}</span>
            </div>
          )}
          {parseFloat(schedule.amount_paid || 0) > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 2 }}>
              <span style={{ color: 'var(--text-3)' }}>Already Paid</span>
              <span style={{ fontWeight: 600, color: '#00C878' }}>{formatCurrency(schedule.amount_paid)}</span>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, paddingTop: 6, borderTop: '1px solid var(--border)' }}>
            <span style={{ color: 'var(--text-2)', fontWeight: 600 }}>Remaining</span>
            <span style={{ fontWeight: 700, color: '#5B8EFF', fontSize: '0.9rem' }}>{formatCurrency(remaining)}</span>
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Payment Amount (₦)</label>
          <input
            className="form-input"
            type="number"
            step="0.01"
            min="1"
            max={remaining}
            value={amount}
            onChange={e => setAmount(e.target.value)}
          />
        </div>

        <div className="form-group">
          <label className="form-label">Payment Method</label>
          <select className="form-input" value={method} onChange={e => setMethod(e.target.value)}>
            <option value="bank_transfer">Bank Transfer</option>
            <option value="card">Card Payment</option>
            <option value="ussd">USSD</option>
            <option value="direct_debit">Direct Debit</option>
          </select>
        </div>

        {err && <div className="alert alert-error" style={{ marginBottom: '0.75rem' }}><span>⚠</span><span>{err}</span></div>}

        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button
            className="btn btn-primary"
            style={{ flex: 1 }}
            disabled={mutation.isPending || !amount || parseFloat(amount) <= 0}
            onClick={() => mutation.mutate({ amount: parseFloat(amount), method, schedule_id: schedule.id })}
          >
            {mutation.isPending ? 'Processing…' : 'Confirm Payment'}
          </button>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

export default function Repayments() {
  const { id: loanId } = useParams();
  const [payingSchedule, setPayingSchedule] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');

  const { data: schedules = [], isLoading } = useQuery({
    queryKey: ['repayment-schedule', loanId],
    queryFn: () => loanService.getRepaymentSchedule(loanId).then(r => {
      const d = r.data.data;
      return Array.isArray(d) ? d : (d?.results ?? []);
    }),
    refetchInterval: 30_000,  // auto-refresh every 30s to catch admin confirmations
  });

  const totalDue    = schedules.reduce((s, i) => s + parseFloat(i.amount_due || 0), 0);
  const totalPaid   = schedules.reduce((s, i) => s + parseFloat(i.amount_paid || 0), 0);
  const totalLate   = schedules.reduce((s, i) => s + parseFloat(i.late_fee || 0), 0);
  const outstanding = Math.max(0, totalDue + totalLate - totalPaid);
  const paidCount   = schedules.filter(s => s.status === 'paid').length;
  const overdueCount = schedules.filter(s => s.status === 'overdue').length;

  const sorted    = [...schedules].sort((a, b) => a.installment_number - b.installment_number);
  const actionable = (s) => ['pending', 'partial', 'overdue'].includes(s.status);

  return (
    <div>
      <div className="page-header">
        <div>
          <div style={{ fontSize: '0.78rem', color: 'var(--text-3)', marginBottom: '0.2rem' }}>
            <Link to={`/loans/${loanId}`} style={{ color: 'var(--text-3)', textDecoration: 'none' }}>← Loan Details</Link>
          </div>
          <h1>Repayment Schedule</h1>
        </div>
      </div>

      {successMsg && (
        <div className="alert alert-success" style={{ marginBottom: '1rem' }}>
          <span>✓</span><span>{successMsg}</span>
        </div>
      )}

      {!isLoading && schedules.some(s => s.payments?.some(p => p.payment_status === 'pending')) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', background: 'rgba(91,142,255,0.08)', border: '1px solid rgba(91,142,255,0.25)', borderRadius: 'var(--radius)', padding: '0.65rem 1rem', marginBottom: '1rem', fontSize: '0.82rem' }}>
          <span style={{ fontSize: '1rem' }}>⏳</span>
          <span style={{ color: '#5B8EFF', fontWeight: 600 }}>Payment being processed</span>
          <span style={{ color: 'var(--text-3)' }}>— Your payment has been submitted and is awaiting confirmation from our team. This page refreshes automatically.</span>
        </div>
      )}

      {/* Summary strip */}
      {!isLoading && schedules.length > 0 && (
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
          {[
            { label: 'Total Due',    value: formatCurrency(totalDue),    color: 'var(--text-1)' },
            { label: 'Total Paid',   value: formatCurrency(totalPaid),   color: '#00C878' },
            { label: 'Outstanding',  value: formatCurrency(outstanding), color: outstanding > 0 ? '#F5A623' : '#00C878' },
            { label: 'Late Fees',    value: formatCurrency(totalLate),   color: totalLate > 0 ? '#FF4757' : 'var(--text-3)' },
            { label: 'Progress',     value: `${paidCount}/${schedules.length} paid`, color: '#5B8EFF' },
            ...(overdueCount > 0 ? [{ label: 'Overdue', value: `${overdueCount} installment${overdueCount > 1 ? 's' : ''}`, color: '#FF4757' }] : []),
          ].map(k => (
            <div key={k.label} style={{ background: 'var(--surface-alt)', borderRadius: 'var(--radius)', padding: '0.6rem 0.875rem', flex: 1, minWidth: 120 }}>
              <div style={{ fontSize: '0.6rem', fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 3 }}>{k.label}</div>
              <div style={{ fontSize: '0.9rem', fontWeight: 700, color: k.color }}>{k.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Progress bar */}
      {!isLoading && schedules.length > 0 && (
        <div className="card" style={{ padding: '0.75rem 1rem', marginBottom: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', marginBottom: '0.3rem' }}>
            <span style={{ color: 'var(--text-2)' }}>{paidCount} of {schedules.length} installments paid</span>
            <strong style={{ color: paidCount === schedules.length ? '#00C878' : '#5B8EFF' }}>
              {Math.round((paidCount / schedules.length) * 100)}%
            </strong>
          </div>
          <div style={{ height: 7, borderRadius: 4, background: 'var(--border)', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${(paidCount / schedules.length) * 100}%`, background: paidCount === schedules.length ? '#00C878' : 'linear-gradient(90deg,#5B8EFF,#00C896)', borderRadius: 4, transition: 'width 0.4s' }} />
          </div>
        </div>
      )}

      {/* Schedule table */}
      <div className="card" style={{ padding: 0 }}>
        {isLoading ? (
          <div style={{ padding: '2rem' }}><LoadingSpinner /></div>
        ) : schedules.length === 0 ? (
          <div style={{ padding: '2.5rem', textAlign: 'center', color: 'var(--text-3)' }}>
            No repayment schedule found. Schedule is generated when the loan is disbursed.
          </div>
        ) : (
          <div className="table-wrapper">
            <table style={{ fontSize: '0.8rem' }}>
              <thead>
                <tr>
                  {['#', 'Due Date', 'Principal', 'Interest', 'Amount Due', 'Paid', 'Late Fee', 'Balance', 'Status', ''].map(h => (
                    <th key={h} style={{ padding: '0.5rem 0.75rem', fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-3)', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sorted.map((s, idx) => {
                  const isOverdue   = s.status === 'overdue';
                  const isPaid      = s.status === 'paid';
                  const lateFee     = parseFloat(s.late_fee || 0);
                  const paid        = parseFloat(s.amount_paid || 0);
                  const hasPending  = s.payments?.some(p => p.payment_status === 'pending');
                  const displayStatus = hasPending && !isPaid ? 'awaiting' : s.status;

                  const prev = idx > 0 ? sorted[idx - 1] : null;
                  const prevComplete = !prev || ['paid', 'waived'].includes(prev.status);

                  return (
                    <tr key={s.id} style={{ background: isOverdue ? 'rgba(255,71,87,0.03)' : isPaid ? 'rgba(0,200,120,0.02)' : hasPending ? 'rgba(91,142,255,0.03)' : 'transparent', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                      <td style={{ padding: '0.5rem 0.75rem', fontWeight: 800, textAlign: 'center', color: isPaid ? '#00C878' : isOverdue ? '#FF4757' : '#5B8EFF' }}>
                        {isPaid ? '✓' : s.installment_number}
                      </td>
                      <td style={{ padding: '0.5rem 0.75rem', whiteSpace: 'nowrap', color: isOverdue ? '#FF4757' : 'var(--text-2)', fontWeight: isOverdue ? 600 : 400 }}>
                        {formatDate(s.due_date)}
                        {s.paid_at && <div style={{ fontSize: '0.65rem', color: 'var(--text-3)' }}>Paid {formatDate(s.paid_at)}</div>}
                      </td>
                      <td style={{ padding: '0.5rem 0.75rem', color: 'var(--text-2)' }}>{formatCurrency(s.principal_due)}</td>
                      <td style={{ padding: '0.5rem 0.75rem', color: '#F5A623' }}>{formatCurrency(s.interest_due)}</td>
                      <td style={{ padding: '0.5rem 0.75rem', fontWeight: 700, color: 'var(--text-1)', textDecoration: isPaid ? 'line-through' : 'none', textDecorationColor: 'var(--text-3)' }}>
                        {formatCurrency(s.amount_due)}
                      </td>
                      <td style={{ padding: '0.5rem 0.75rem', color: paid > 0 ? '#00C878' : 'var(--text-3)', fontWeight: paid > 0 ? 600 : 400 }}>
                        {paid > 0 ? formatCurrency(paid) : '—'}
                      </td>
                      <td style={{ padding: '0.5rem 0.75rem', color: lateFee > 0 ? '#FF4757' : 'var(--text-3)', fontWeight: lateFee > 0 ? 600 : 400 }}>
                        {lateFee > 0 ? formatCurrency(lateFee) : '—'}
                      </td>
                      <td style={{ padding: '0.5rem 0.75rem', color: isPaid ? 'var(--text-3)' : 'var(--text-2)' }}>
                        {formatCurrency(s.balance)}
                      </td>
                      <td style={{ padding: '0.5rem 0.75rem' }}><StatusPill status={displayStatus} /></td>
                      <td style={{ padding: '0.5rem 0.75rem' }}>
                        {hasPending && !isPaid ? (
                          <span style={{ fontSize: '0.68rem', color: '#5B8EFF', fontStyle: 'italic', whiteSpace: 'nowrap' }}>
                            ⏳ Processing…
                          </span>
                        ) : actionable(s) && !prevComplete ? (
                          <span
                            title={`Complete installment #${prev.installment_number} first`}
                            style={{ fontSize: '0.75rem', color: 'var(--text-3)', whiteSpace: 'nowrap', cursor: 'default' }}
                          >
                            🔒 Locked
                          </span>
                        ) : actionable(s) ? (
                          <button
                            className="btn btn-primary"
                            style={{ fontSize: '0.7rem', padding: '0.25rem 0.6rem', whiteSpace: 'nowrap' }}
                            onClick={() => setPayingSchedule(s)}
                          >
                            Pay
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid var(--border)', background: 'var(--surface-alt)', fontWeight: 700 }}>
                  <td colSpan={4} style={{ padding: '0.5rem 0.75rem', fontSize: '0.65rem', color: 'var(--text-3)', textTransform: 'uppercase', fontWeight: 700 }}>Totals</td>
                  <td style={{ padding: '0.5rem 0.75rem', color: 'var(--text-1)' }}>{formatCurrency(totalDue)}</td>
                  <td style={{ padding: '0.5rem 0.75rem', color: '#00C878' }}>{formatCurrency(totalPaid)}</td>
                  <td style={{ padding: '0.5rem 0.75rem', color: totalLate > 0 ? '#FF4757' : 'var(--text-3)' }}>{formatCurrency(totalLate)}</td>
                  <td style={{ padding: '0.5rem 0.75rem', color: outstanding > 0 ? '#F5A623' : '#00C878' }}>{formatCurrency(outstanding)}</td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {payingSchedule && (
        <PayModal
          schedule={payingSchedule}
          loanId={loanId}
          onClose={() => { setPayingSchedule(null); setSuccessMsg('Payment submitted — awaiting admin confirmation. You will be notified once confirmed.'); setTimeout(() => setSuccessMsg(''), 8000); }}
        />
      )}
    </div>
  );
}
